import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import useAxios from '@/services/api/useAxios'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'

const resource = 'elearningCourses'

const filter = (axios: IFactoryFunctionRequest<any, any>) => (query: Record<string, any>) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/filter`,
    query,
  })
}

const getOne = (axios: IFactoryFunctionRequest<any, any>) => (courseId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${courseId}`,
  })
}

const createOne = (axios: IFactoryFunctionRequest<any, any>) => (data: Record<string, any>) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    data,
  })
}

const updateOne = (axios: IFactoryFunctionRequest<any, any>) => (courseId: string, data: Record<string, any>) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${courseId}`,
    data,
  })
}

const deleteOne = (axios: IFactoryFunctionRequest<any, any>) => (courseId: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${courseId}`,
  })
}

/** Creates a draft course whose pages are built in the background from the document (see course.importJob). */
const importFromFile = (axios: IFactoryFunctionRequest<any, any>) => (file: File) => {
  const formData = new FormData()
  formData.append('file', file)
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/import`,
    data: formData,
  })
}

/** Brings the unfinished assignments of the course up to date (see syncAssignments in the API). */
const syncAssignments = (axios: IFactoryFunctionRequest<any, any>) => (courseId: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${courseId}/syncAssignments`,
  })
}

/** Audio state of every page of one language version of the course (see elearningCourse/audio in the API). */
const listAudio = (axios: IFactoryFunctionRequest<any, any>) => (courseId: string, language?: string | null) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${courseId}/audio`,
    query: language ? { language } : {},
  })
}

/** Language versions of the course (see elearningCourse/translation in the API). */
const listTranslations = (axios: IFactoryFunctionRequest<any, any>) => (courseId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${courseId}/translations`,
  })
}

/** Machine translation into a language, made in the background. */
const translate = (axios: IFactoryFunctionRequest<any, any>) => (courseId: string, language: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${courseId}/translations`,
    data: { language },
  })
}

const updateTranslation = (axios: IFactoryFunctionRequest<any, any>) => (courseId: string, language: string, data: { display: string, description: string, pages: Array<{ title: string, contentHtml: string, blocks: any[] }> }) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${courseId}/translations/${language}`,
    data,
  })
}

const deleteTranslation = (axios: IFactoryFunctionRequest<any, any>) => (courseId: string, language: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${courseId}/translations/${language}`,
  })
}

/** Queues the audio of the pages: made in the background, followed through listAudio. */
const generateAudio = (axios: IFactoryFunctionRequest<any, any>) => (courseId: string, data: { mode: string, language: string, voiceIds: string[], pageIds: string[] }) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${courseId}/audio`,
    data,
  })
}

const deleteAudio = (axios: IFactoryFunctionRequest<any, any>) => (courseId: string, audioId: string) => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${courseId}/audio/${audioId}`,
  })
}

const listAudioVoices = (axios: IFactoryFunctionRequest<any, any>) => (language: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/audio/voices`,
    query: { language },
  })
}

/** Finished audios of the pages of an assignment, for its player. */
const assignmentAudio = (axios: IFactoryFunctionRequest<any, any>) => (assignmentId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/audio/assignment/${assignmentId}`,
  })
}

export default (axios: AxiosInstance) => ({
  filter: filter(useAxios(axios)),
  getOne: getOne(useAxios(axios)),
  createOne: createOne(useAxios(axios)),
  updateOne: updateOne(useAxios(axios)),
  deleteOne: deleteOne(useAxios(axios)),
  importFromFile: importFromFile(useAxios(axios)),
  syncAssignments: syncAssignments(useAxios(axios)),
  listAudio: listAudio(useAxios(axios)),
  listTranslations: listTranslations(useAxios(axios)),
  translate: translate(useAxios(axios)),
  updateTranslation: updateTranslation(useAxios(axios)),
  deleteTranslation: deleteTranslation(useAxios(axios)),
  generateAudio: generateAudio(useAxios(axios)),
  deleteAudio: deleteAudio(useAxios(axios)),
  listAudioVoices: listAudioVoices(useAxios(axios)),
  assignmentAudio: assignmentAudio(useAxios(axios)),
})
